import { useQuery } from '@apollo/client';
import { getSubAccountName } from '@interfaces/subAccount';
import Fade from '@mui/material/Fade';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { useConfirmationDialog } from '@ovComponents/4-module/workflowCompletion/components/useConfirmationDialog';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Divider, IconButton, Typography, useTheme } from 'hephaestus/web';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from 'hephaestus/webIcons';
import {
  type ComponentProps,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { FETCH_SUBACCOUNT_DETAILS } from './depositReview.queries';
import type { DepositReviewSubstepOptions } from './depositReview.visual';

const FieldDisplay = ({
  title,
  children,
  stacked = true,
}: {
  title: string;
  children: ReactNode;
  stacked?: boolean;
}) => (
  <Box
    sx={
      stacked
        ? {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.5,
            width: '100%',
          }
        : { display: 'contents' }
    }
  >
    <Typography
      variant='bodyMedium'
      sx={{
        fontWeight: 600,
        ...(stacked ? {} : { whiteSpace: 'nowrap' }),
      }}
    >
      {title}
    </Typography>
    <Typography variant='bodyMedium' sx={stacked ? undefined : { minWidth: 0 }}>
      {children}
    </Typography>
  </Box>
);

export const DisclaimerBox = ({ text }: { text: string }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 1,
        background: theme.color.surfaceContainerVariant,
        borderRadius: theme.borderRadius.md,
      }}
    >
      <Typography variant='bodySmall' component='div' sx={{ mx: 1, my: -1 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {text}
        </ReactMarkdown>
      </Typography>
    </Box>
  );
};

type LegacyAccountTsType = Parameters<typeof getSubAccountName>[0];

// Collapse would hide this panel when closed, and the compact row would leave with it.
const KeepAccordionDetailsMounted = ({ children }: { children?: ReactNode }) =>
  children;

export const DepositCard = ({
  deposit,
  onDepositDeleted,
  onDepositEdit,
  enableDelete = true,
  enableEdit = true,
  options,
}: {
  deposit: TransferContextData;
  index: number;
  onDepositDeleted: (temporaryId: string) => void;
  onDepositEdit: (temporaryId: string) => void;
  enableDelete?: boolean;
  enableEdit?: boolean;
  options: DepositReviewSubstepOptions;
}) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const compactRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>();
  const { t } = useTranslation(['workflowCompletions', 'accountTypes']);
  const { translateBackendString, localizedDate } = useLocalization();
  const { confirmationDialog, getUserConfirmation } = useConfirmationDialog();

  // Fetch detailed subaccount information for proper display
  const { data: subAccountData } = useQuery(FETCH_SUBACCOUNT_DETAILS, {
    variables: { subAccountId: deposit.subAccount?.id as string },
    skip: !deposit.subAccount?.id,
  });

  const { data: fromSubAccountData } = useQuery(FETCH_SUBACCOUNT_DETAILS, {
    variables: { subAccountId: deposit.fromSubAccount?.id as string },
    skip: !deposit.fromSubAccount?.id,
  });

  // Use detailed data if available, fallback to partial data
  const detailedSubAccount =
    subAccountData?.fetchSubAccount?.subAccount || deposit.subAccount;
  const detailedFromSubAccount =
    fromSubAccountData?.fetchSubAccount?.subAccount || deposit.fromSubAccount;

  useLayoutEffect(() => {
    const node = expanded ? expandedRef.current : compactRef.current;
    if (!node || node.offsetHeight === 0) return;
    setContentHeight(node.offsetHeight);
  }, [expanded, deposit, subAccountData, fromSubAccountData]);

  const formatAmount = (amountCents?: number) => {
    return formatMoneyValue(amountCents, '$', 2, false);
  };

  const fullTransfer = () =>
    detailedFromSubAccount?.statistics?.marketValueCents &&
    (deposit.amountCents || 0) >
      (detailedFromSubAccount?.statistics?.marketValueCents || 0) * 0.97;

  const getDepositTypeLabel = (type?: string) => {
    switch (type) {
      case 'EFT':
        return t('depositTypeOptions.EFT');
      case 'INTERNAL_TRANSFER':
        return t('depositTypeOptions.INTERNAL_TRANSFER');
      case 'EXTERNAL_TRANSFER':
        return t('depositTypeOptions.EXTERNAL_TRANSFER');
      default:
        return type || t('depositReview.unknownDepositType');
    }
  };

  const getDepositSourceLabel = () => {
    switch (deposit.type) {
      case 'EFT':
        return t('depositReview.fromBankAccount');
      case 'INTERNAL_TRANSFER':
        return t('depositReview.fromSubAccount');
      case 'EXTERNAL_TRANSFER':
        return t('depositReview.financialInstitution');
      default:
        return 'From';
    }
  };

  const getDepositSource = () => {
    switch (deposit.type) {
      case 'EFT':
        if (
          deposit.bankAccount?.name &&
          deposit.bankAccount?.bankAccountNumber
        ) {
          const last4 = deposit.bankAccount.bankAccountNumber.slice(-4);
          return `${deposit.bankAccount.name} ***${last4}`;
        }
        return 'Bank Account';
      case 'INTERNAL_TRANSFER': {
        const fromSubAccountName = getSubAccountName(
          translateBackendString,
          detailedFromSubAccount as LegacyAccountTsType,
        );
        return fromSubAccountName;
      }
      case 'EXTERNAL_TRANSFER':
        if (deposit.institution?.name && deposit.accountNumber) {
          const last4 = deposit.accountNumber.slice(-4);
          return `${deposit.institution.name} ***${last4}`;
        }
        return 'External Account';
      default:
        return 'Unknown Source';
    }
  };

  const disclaimer = (() => {
    if (deposit.type === 'EFT' && options.eftDisclaimer) {
      return (
        <DisclaimerBox text={translateBackendString(options.eftDisclaimer)} />
      );
    }
    if (
      deposit.type === 'INTERNAL_TRANSFER' &&
      options.internalTransferDisclaimer
    ) {
      return (
        <DisclaimerBox
          text={translateBackendString(options.internalTransferDisclaimer)}
        />
      );
    }
    if (
      deposit.type === 'EXTERNAL_TRANSFER' &&
      options.externalTransferDisclaimer
    ) {
      return (
        <DisclaimerBox
          text={translateBackendString(options.externalTransferDisclaimer)}
        />
      );
    }
    return null;
  })();

  const depositDisplayTitle = t('depositReview.depositDisplayTitle', {
    amount: formatAmount(deposit.amountCents),
    account: getSubAccountName(
      translateBackendString,
      detailedSubAccount as LegacyAccountTsType,
    ),
  });

  const onDeleteButtonClick: ComponentProps<typeof IconButton>['onClick'] =
    async (e) => {
      e.stopPropagation();
      const userConfirmation = await getUserConfirmation({
        title: t('depositReview.deleteDepositConfirmation.title', {
          depositDisplayTitle,
          amount: formatAmount(deposit.amountCents),
        }),
        bodyText: t('depositReview.deleteDepositConfirmation.bodyText', {
          depositDisplayTitle,
          amount: formatAmount(deposit.amountCents),
        }),
        setConfirmButtonColor: 'error',
      });
      if (!userConfirmation.confirmed) {
        userConfirmation.closeDialog();
        return;
      }
      if (deposit.temporaryId) {
        await onDepositDeleted(deposit.temporaryId);
      }
      userConfirmation.closeDialog();
    };

  const onEditButtonClick: ComponentProps<typeof IconButton>['onClick'] =
    async (e) => {
      e.stopPropagation();
      if (deposit.temporaryId) {
        await onDepositEdit(deposit.temporaryId);
      }
    };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      slots={{ transition: KeepAccordionDetailsMounted }}
      variant='workflow'
      sx={{ borderRadius: `${theme.borderRadius.md} !important` }}
      state={
        { failed: 'error' as const, completed: undefined }[
          deposit.temporaryTransferState!
        ]
      }
    >
      <AccordionSummary
        sx={{
          borderTop: 'none !important',
          padding: '16px 16px 0',
          minHeight: 'auto !important',
          '& .MuiAccordionSummary-content': {
            margin: '0 !important',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              flex: 1,
            }}
          >
            {deposit.temporaryTransferState === 'completed' && (
              <CheckCircleIcon
                sx={{ color: 'success.main', fontSize: '18px', mr: 1 }}
              />
            )}
            {deposit.temporaryTransferState === 'failed' && (
              <CancelIcon
                sx={{ color: 'error.main', fontSize: '18px', mr: 1 }}
              />
            )}
            <Typography variant='titleMedium'>{depositDisplayTitle}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              alignItems: 'center',
              gap: 1,
            }}
          >
            {enableDelete && deposit.temporaryTransferState !== 'completed' && (
              <IconButton
                aria-label='delete'
                size='xsmall'
                onClick={onDeleteButtonClick}
                sx={{ p: 1, mx: -1 }}
              >
                <DeleteIcon sx={{ fontSize: '16px' }} fontSize='inherit' />
              </IconButton>
            )}
            {enableEdit && deposit.temporaryTransferState !== 'completed' && (
              <IconButton
                aria-label='edit'
                size='xsmall'
                onClick={onEditButtonClick}
                sx={{ p: 1, mx: -1 }}
              >
                <EditIcon sx={{ fontSize: '16px' }} fontSize='inherit' />
              </IconButton>
            )}
            <ExpandMoreIcon
              sx={{
                fontSize: '24px',
                color: 'text.primary',
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 200ms ease',
              }}
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: '0 16px 16px',
          userSelect: 'text',
        }}
      >
        <Box
          onClick={expanded ? undefined : () => setExpanded(true)}
          sx={{
            mt: 1.5,
            cursor: expanded ? 'auto' : 'pointer',
          }}
        >
          <Divider />
          <Box
            sx={{
              display: 'grid',
              mt: 1.5,
              alignItems: 'start',
              overflow: 'hidden',
              height: contentHeight,
              transition: 'height 200ms ease',
              '& > *': { gridArea: '1 / 1' },
            }}
          >
            <Fade in={!expanded} appear={false} unmountOnExit timeout={200}>
              <Box
                ref={compactRef}
                sx={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr',
                  columnGap: 1.5,
                  rowGap: 1.25,
                  alignItems: 'center',
                }}
              >
                <FieldDisplay stacked={false} title={t('shared:type')}>
                  {getDepositTypeLabel(deposit.type)}
                </FieldDisplay>
                <FieldDisplay stacked={false} title={getDepositSourceLabel()}>
                  {getDepositSource()}
                </FieldDisplay>
              </Box>
            </Fade>
            <Fade in={expanded} appear={false} unmountOnExit timeout={200}>
              <Box
                ref={expandedRef}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  userSelect: 'text',
                  cursor: 'auto',
                }}
              >
                <FieldDisplay title={t('shared:type')}>
                  {getDepositTypeLabel(deposit.type)}
                </FieldDisplay>
                {deposit.type === 'EFT' && (
                  <>
                    <FieldDisplay title={t('depositReview.fromBankAccount')}>
                      {`${deposit.bankAccount?.name} ${deposit.bankAccount?.bankAccountNumber}`}
                    </FieldDisplay>
                    <FieldDisplay title={t('depositReview.toSubAccount')}>
                      {getSubAccountName(
                        translateBackendString,
                        detailedSubAccount as LegacyAccountTsType,
                      )}
                    </FieldDisplay>
                    {deposit.holdings?.financialProduct && (
                      <FieldDisplay title={t('depositReview.toHolding')}>
                        {translateBackendString(
                          deposit.holdings.financialProduct.translatedName,
                        )}
                      </FieldDisplay>
                    )}
                    <FieldDisplay title={t('depositReview.amount')}>
                      {formatMoneyValue(deposit.amountCents, '$', 2, false)}
                    </FieldDisplay>
                    {deposit.schedule && deposit.schedule !== 'ONE_TIME' && (
                      <>
                        <FieldDisplay title={t('depositReview.schedule')}>
                          {t(`schedule.${deposit.schedule}`)}
                        </FieldDisplay>
                        {deposit.scheduledDate && (
                          <FieldDisplay title={t('depositReview.startsOn')}>
                            {localizedDate(deposit.scheduledDate)}
                          </FieldDisplay>
                        )}
                      </>
                    )}
                    {deposit.schedule === 'ONE_TIME' &&
                      deposit.scheduledDate && (
                        <FieldDisplay title={t('depositReview.scheduledFor')}>
                          {localizedDate(deposit.scheduledDate)}
                        </FieldDisplay>
                      )}
                  </>
                )}
                {deposit.type === 'INTERNAL_TRANSFER' && (
                  <>
                    <FieldDisplay title={t('depositReview.fromSubAccount')}>
                      {getSubAccountName(
                        translateBackendString,
                        detailedFromSubAccount as LegacyAccountTsType,
                      )}
                    </FieldDisplay>
                    <FieldDisplay title={t('depositReview.toSubAccount')}>
                      {getSubAccountName(
                        translateBackendString,
                        detailedSubAccount as LegacyAccountTsType,
                      )}
                    </FieldDisplay>
                    <FieldDisplay title={t('depositReview.amount')}>
                      {fullTransfer()
                        ? t('depositReview.fullTransfer')
                        : formatMoneyValue(deposit.amountCents, '$', 2, false)}
                    </FieldDisplay>
                  </>
                )}
                {deposit.type === 'EXTERNAL_TRANSFER' && (
                  <>
                    <FieldDisplay title={t('depositReview.toSubAccount')}>
                      {getSubAccountName(
                        translateBackendString,
                        detailedSubAccount as LegacyAccountTsType,
                      )}
                    </FieldDisplay>
                    <FieldDisplay
                      title={t('depositReview.financialInstitution')}
                    >
                      {deposit.institution?.name}
                    </FieldDisplay>
                    <FieldDisplay title={t('depositReview.accountNumber')}>
                      {deposit.accountNumber}
                    </FieldDisplay>
                    <FieldDisplay title={t('depositReview.accountType')}>
                      {deposit.accountType
                        ? t(`accountTypes:${deposit.accountType}`)
                        : null}
                    </FieldDisplay>
                    <FieldDisplay title={t('depositReview.transferType')}>
                      {t(`depositReview.${deposit.transferType}`)}
                    </FieldDisplay>
                    <FieldDisplay title={t('depositReview.amount')}>
                      {fullTransfer()
                        ? t('depositReview.fullTransfer')
                        : formatMoneyValue(deposit.amountCents, '$', 2, false)}
                    </FieldDisplay>
                  </>
                )}
                {/* Common fields for all types */}
                {deposit.fiscalYear && (
                  <FieldDisplay title={t('components:fiscalYearTitle')}>
                    {deposit.fiscalYear}
                  </FieldDisplay>
                )}
              </Box>
            </Fade>
          </Box>
        </Box>
        {disclaimer && (
          <Box sx={{ mt: 1.5, userSelect: 'text', cursor: 'auto' }}>
            {disclaimer}
          </Box>
        )}
      </AccordionDetails>
      {confirmationDialog}
    </Accordion>
  );
};
